'use client';

import React from 'react';
import type { QuoteAdvisorySection } from '@/types';
import type { Advisory } from '../../services/advisoryRules';
import { AlertTriangle, CheckCircle2, Info } from 'lucide-react';

type SectionAdvisoriesProps = {
  title: string;
  section: QuoteAdvisorySection;
  advisories: Advisory[];
  notes?: string;
  compact?: boolean;
};

const severityTone: Record<string, string> = {
  LOW: 'border-slate-200 bg-slate-50 text-slate-700',
  MEDIUM: 'border-amber-200 bg-amber-50 text-amber-800',
  HIGH: 'border-rose-200 bg-rose-50 text-rose-800',
};

const SectionAdvisories: React.FC<SectionAdvisoriesProps> = ({
  title,
  section,
  advisories,
  notes,
  compact = false,
}) => {
  const items = advisories.filter(item => item.section === section);
  if (!items.length && !notes) {
    return (
      <div className={`rounded-[1.25rem] border border-emerald-200/80 bg-emerald-50/80 ${compact ? 'px-3 py-2.5' : 'px-4 py-3'}`}>
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-emerald-700">{title}</p>
            <p className="text-xs font-semibold text-emerald-700">No active issues in this section.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-2 rounded-[1.25rem] border border-slate-200/80 bg-white/85 ${compact ? 'p-3' : 'p-4'} shadow-[0_10px_24px_rgba(15,23,42,0.04)]`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Info className="h-4 w-4 text-indigo-600" />
          <div>
            <p className="text-[10px] font-black uppercase tracking-[0.18em] text-indigo-600">{title}</p>
            <p className="text-xs font-semibold text-slate-500">{items.length} advisory{items.length !== 1 ? 'ies' : ''}</p>
          </div>
        </div>
      </div>
      {notes ? (
        <div className="rounded-xl border border-sky-200/70 bg-sky-50/80 px-3 py-2.5 text-xs leading-5 text-sky-800">
          {notes}
        </div>
      ) : null}
      {items.map(item => (
        <div key={`${item.id}-${item.legId || 'quote'}`} className={`rounded-xl border px-3 py-2.5 ${severityTone[item.severity] || severityTone.LOW}`}>
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <div className="min-w-0">
              <p className="text-[11px] font-black uppercase tracking-[0.14em]">{item.title}</p>
              <p className="mt-1 text-xs leading-5">{item.detail}</p>
              {item.suggestion ? (
                <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.12em] opacity-80">
                  Fix: {item.suggestion}
                </p>
              ) : null}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default SectionAdvisories;
